import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms';
}
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Responsable del tratamiento',
    body: 'El responsable del tratamiento de tus datos personales es Protocolo Vientre Plano 50+. Nos comprometemos a proteger tu privacidad y a tratar tus datos de acuerdo con el Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos (LOPD-GDD).'
  }, {
    heading: 'Datos que recopilamos',
    body: 'Recopilamos únicamente los datos necesarios para procesar tu compra y enviarte el producto digital: nombre, dirección de correo electrónico y datos de pago. Los datos de pago son procesados de forma segura por nuestro proveedor de pagos y no almacenamos información de tarjetas de crédito.'
  }, {
    heading: 'Finalidad del tratamiento',
    body: 'Tus datos serán utilizados exclusivamente para: gestionar tu pedido y entregarte el producto digital adquirido, enviarte comunicaciones relacionadas con tu compra, y cumplir con las obligaciones legales aplicables.'
  }, {
    heading: 'Base legal',
    body: 'La base legal para el tratamiento de tus datos es la ejecución del contrato de compraventa del producto digital, así como tu consentimiento expreso cuando sea necesario.'
  }, {
    heading: 'Conservación de datos',
    body: 'Tus datos personales serán conservados durante el tiempo necesario para cumplir con la finalidad para la que fueron recogidos y para cumplir con las obligaciones legales vigentes.'
  }, {
    heading: 'Tus derechos',
    body: 'Tienes derecho a acceder, rectificar, suprimir, limitar el tratamiento, portar y oponerte al tratamiento de tus datos personales. Puedes ejercer estos derechos enviando una solicitud a través de los canales habilitados.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Objeto',
    body: 'Los presentes términos y condiciones regulan la compra y el uso del producto digital "Protocolo Vientre Plano 50+", un programa informativo en formato PDF destinado a mujeres mayores de 50 años.'
  }, {
    heading: 'Naturaleza del producto',
    body: 'Este producto es un recurso educativo e informativo. No constituye asesoramiento médico, diagnóstico ni tratamiento. Consulta siempre con un profesional de la salud antes de realizar cambios en tu dieta o estilo de vida.'
  }, {
    heading: 'Proceso de compra',
    body: 'Una vez realizado el pago, recibirás acceso inmediato al producto digital a través del correo electrónico proporcionado. Es tu responsabilidad asegurar que la dirección de correo electrónico facilitada sea correcta.'
  }, {
    heading: 'Política de devoluciones',
    body: 'Dado que se trata de un producto digital de descarga inmediata, y de acuerdo con la legislación vigente sobre contenido digital, no se admiten devoluciones una vez que el contenido ha sido descargado o accedido.'
  }, {
    heading: 'Propiedad intelectual',
    body: 'Todo el contenido del protocolo está protegido por derechos de propiedad intelectual. Queda prohibida su reproducción, distribución, comunicación pública o transformación sin autorización expresa.'
  }, {
    heading: 'Limitación de responsabilidad',
    body: 'Los resultados pueden variar de una persona a otra. No garantizamos resultados específicos. El uso del protocolo es bajo tu propia responsabilidad y no sustituye el consejo médico profesional.'
  }, {
    heading: 'Legislación aplicable',
    body: 'Estos términos se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales competentes.'
  }]
};
export function LegalModal({
  isOpen,
  onClose,
  type
}: LegalModalProps) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-104">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-105">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#2D2A26]/60 backdrop-blur-sm" data-id="element-106" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-[#FAF7F2] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-id="element-107">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-[#E8EDE6] bg-white shrink-0" data-id="element-108">
              <h2 className="text-xl md:text-2xl font-bold text-[#2D2A26] font-serif" data-id="element-109">
                {content.title}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-[#E8EDE6] transition-colors duration-200" aria-label="Cerrar" data-id="element-110">
                <XIcon className="w-5 h-5 text-[#6B6560]" data-id="element-111" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 space-y-6" data-id="element-112">
              {content.sections.map((section, idx) => <div key={idx} data-id="element-113">
                  <h3 className="text-lg font-bold text-[#4A6741] mb-2 font-serif" data-id="element-114">
                    {section.heading}
                  </h3>
                  <p className="text-[#6B6560] leading-relaxed" data-id="element-115">
                    {section.body}
                  </p>
                </div>)}

              <p className="text-sm text-[#6B6560]/70 pt-4 border-t border-[#E8EDE6]" data-id="element-116">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long'
            })}
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}